import { PassphraseIssue, normalizePassphrase, validatePassphrase } from '@wiztime/sync';

/** What the join form tells the user about the text typed or pasted so far. */
export const PassphraseCheckKind = {
  /** Nothing typed yet. */
  Empty: 'empty',
  /** Fewer or more than 12 words so far. */
  WordCount: 'wordCount',
  /** A word is not in the word list. */
  UnknownWord: 'unknownWord',
  /** All words exist but the built-in checksum fails: a word is mistyped or in the wrong place. */
  Checksum: 'checksum',
  Valid: 'valid',
} as const;
export type PassphraseCheckKind = (typeof PassphraseCheckKind)[keyof typeof PassphraseCheckKind];

export interface PassphraseCheck {
  readonly kind: PassphraseCheckKind;
  /** Words entered so far. */
  readonly count: number;
  /** Position (1-based) and text of the first unknown word. */
  readonly position?: number;
  readonly word?: string;
  /** The canonical passphrase when valid. */
  readonly normalized?: string;
}

/** Live validation of a passphrase being entered, with the position of the first unknown word. */
export function checkPassphraseText(text: string): PassphraseCheck {
  const normalized = normalizePassphrase(text);
  const words = normalized === '' ? [] : normalized.split(' ');
  if (words.length === 0) return { kind: PassphraseCheckKind.Empty, count: 0 };
  const result = validatePassphrase(text);
  if (result.valid)
    return { kind: PassphraseCheckKind.Valid, count: words.length, normalized: result.normalized };
  switch (result.reason) {
    case PassphraseIssue.UnknownWord: {
      const index = result.wordIndex ?? 0;
      return {
        kind: PassphraseCheckKind.UnknownWord,
        count: words.length,
        position: index + 1,
        word: words[index] ?? '',
      };
    }
    case PassphraseIssue.Checksum:
      return { kind: PassphraseCheckKind.Checksum, count: words.length };
    case PassphraseIssue.WordCount:
      return { kind: PassphraseCheckKind.WordCount, count: words.length };
  }
}
